/**
 * GTIN — Global Trade Item Number.
 *
 * The barcode on a product. Four lengths are in use and all four share one
 * check-digit algorithm:
 *
 *   GTIN-8    small packages
 *   GTIN-12   UPC, mostly North America
 *   GTIN-13   EAN, the usual one in Brazil
 *   GTIN-14   outer cases and pallets
 *
 * The algorithm, from the GS1 check digit specification:
 *
 *   1. weight each digit, alternating ×3 and ×1, anchored so that the digit
 *      immediately to the left of the check digit is ×3;
 *   2. sum;
 *   3. the check digit is what raises that sum to the next multiple of ten.
 *
 * Anchoring from the right rather than the left is what makes one function
 * cover all four lengths: the weights fall on the same positions whether the
 * number is eight digits or fourteen.
 *
 * Verified against the two worked examples GS1 US publishes:
 *   GTIN-13  629104150021 → 3   (sum 57, 60 − 57)
 *   GTIN-12  61414121022  → 0   (sum 60, already a multiple of ten)
 *
 * A GTIN is stored as a STRING for the same reason a CNPJ is: leading zeros
 * are significant, and `0614141210220` is not 614141210220.
 */
/** Lengths GS1 defines. Anything else is not a GTIN. */
export const GTIN_LENGTHS = [8, 12, 13, 14];
const DIGITS_ONLY = /^[0-9]+$/;
/** Strips the spaces and hyphens people paste from spreadsheets. */
export const normalizeGtin = (input) => input.replace(/[\s.-]/g, '');
/**
 * Computes the check digit for a body — the number without its final digit.
 *
 * Exported because a function that can produce the digit is the honest way to
 * test one that verifies it, and because importing a supplier catalogue
 * sometimes means completing a truncated code.
 */
export const gtinCheckDigit = (body) => {
    const normalized = normalizeGtin(body);
    if (!DIGITS_ONLY.test(normalized)) {
        throw new Error('GTIN body must contain digits only');
    }
    let sum = 0;
    for (let index = 0; index < normalized.length; index += 1) {
        // Distance from the check-digit position, counted from the right. The
        // digit next to it is distance 0 and takes ×3.
        const fromRight = normalized.length - 1 - index;
        const weight = fromRight % 2 === 0 ? 3 : 1;
        sum += Number(normalized[index]) * weight;
    }
    return (10 - (sum % 10)) % 10;
};
export const isValidGtin = (input) => {
    const normalized = normalizeGtin(input);
    if (!DIGITS_ONLY.test(normalized))
        return false;
    if (!GTIN_LENGTHS.includes(normalized.length)) {
        return false;
    }
    /**
     * All zeros is rejected.
     *
     * It satisfies the check digit — the sum is zero — and it is what an empty
     * field padded to thirteen characters looks like. Same reasoning as the
     * repeated-character rule for CNPJ.
     */
    if (/^0+$/.test(normalized))
        return false;
    const body = normalized.slice(0, -1);
    const declared = Number(normalized.slice(-1));
    return gtinCheckDigit(body) === declared;
};
/**
 * O código que a loja aceita guardar como código de barras — F168.
 *
 * Nem todo código de barras é um GTIN. As etiquetas da Elite Digital trazem o
 * número do sistema antigo (`40`, `91830`, `0000000000010`), que não tem dígito
 * verificador nenhum: o GDOOR imprimia o código do produto e pronto. A migração
 * 0093 já aceitava esses números no banco, e a importação da F165/F166 gravou
 * 5.531 deles — mas o cadastro à mão continuou exigindo GTIN de verdade, e foi
 * assim que o Eduardo descobriu, em 20/09, que não conseguia salvar um produto
 * inteiro por causa do código que ele mesmo acabara de importar.
 *
 * A regra aqui é a do banco, e não a da GS1: só dígitos, de 1 a 20, e nunca só
 * zeros — que é o que um campo vazio preenchido à força parece.
 *
 * `isValidGtin` continua existindo e continua sendo a pergunta certa em outro
 * lugar: é ela que diz se o número pode ir na NF-e como `cEAN`. O que ela não
 * pode fazer é impedir a loja de guardar o número que está colado na prateleira.
 */
export const isStorableBarcode = (input) => {
    const normalized = normalizeGtin(input);
    if (!DIGITS_ONLY.test(normalized))
        return false;
    if (normalized.length > 20)
        return false;
    return !/^0+$/.test(normalized);
};
/**
 * Widens a shorter GTIN to fourteen digits by left-padding with zeros.
 *
 * GS1 defines GTIN-8, -12 and -13 as GTIN-14 values with leading zeros, which
 * makes GTIN-14 the right storage form for comparison: a GTIN-13 printed on a
 * unit and the GTIN-14 printed on its case are then comparable as strings
 * rather than through a length-aware equality function scattered everywhere.
 *
 * Not used for storage here — the code is kept exactly as the supplier
 * provides it, because that is what an operator reads off the box — but
 * available for the lookup that will eventually need it.
 */
export const toGtin14 = (input) => {
    const normalized = normalizeGtin(input);
    if (!isValidGtin(normalized))
        return null;
    return normalized.padStart(14, '0');
};
/**
 * O código de barras interno da loja (F133).
 *
 * O dono pediu o código de barras "certinho, funcional" já gerado no cadastro,
 * para o produto que não traz EAN de fábrica. A GS1 reserva os prefixos `20` a
 * `29` para uso restrito dentro da loja: um EAN-13 começado por `2` nunca é o
 * código de um fabricante, então não colide com o que vem na embalagem.
 *
 * Forma: `2` + o número com onze dígitos + o verificador. O produto `000123`
 * vira `2000000001230`-e-o-dígito-certo, e quem lê o número embaixo das barras
 * reconhece o código do produto no fim dele.
 *
 * Devolve nulo para o que não cabe: um número com mais de onze dígitos, ou que
 * não é número.
 */
export const internalGtin13 = (number) => {
    const digits = typeof number === 'bigint' ? number.toString() : number;
    if (!DIGITS_ONLY.test(digits) || digits.length > 11)
        return null;
    const body = `2${digits.padStart(11, '0')}`;
    return body + String(gtinCheckDigit(body));
};
//# sourceMappingURL=gtin.js.map